import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import React, { Fragment, useCallback } from 'react';
import { Linking, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { AppHeader } from '@/components/layout/app-header';
import { showAlert, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useCoachNotificationPreferences } from '@/features/coach/hooks/use-coach-notification-preferences';
import { NotificationPermissionBanner } from '@/features/guardian-profile/components/notification-preferences/notification-permission-banner';
import { createStyles, useNotificationPermission, useScaling } from '@/hooks';
import type { AuthStackParamList } from '@/navigations/types';
import { colors } from '@/theme/colors';

import {
  COACH_NOTIFICATION_PREFERENCE_ROWS,
  CoachNotificationPreferenceRow,
  CoachNotificationPreferenceRowSkeleton,
} from '../components/notification-preferences';

type Props = NativeStackScreenProps<AuthStackParamList, 'CoachNotificationPreferences'>;

const strings = STRINGS.coach.notificationPreferences;

export function CoachNotificationPreferencesScreen({ navigation }: Props) {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const {
    isDenied,
    isUndetermined,
    canAsk: canAskNotificationPermission,
    isSupported: isNotificationSupported,
    request: requestNotificationPermission,
  } = useNotificationPermission();
  const { preferences, handleToggle, isLoading } = useCoachNotificationPreferences();

  const isWeb = Platform.OS === 'web';
  const showPermissionBanner = isWeb
    ? isNotificationSupported && (isUndetermined || isDenied)
    : isDenied;

  const headerPaddingTop = insets.top + scale({ phone: 12, tablet: 21 });

  const handlePermissionPress = useCallback(() => {
    if (!isWeb) {
      Linking.openSettings();
      return;
    }
    if (canAskNotificationPermission) {
      requestNotificationPermission();
      return;
    }
    showAlert({
      title: STRINGS.common.noticeTitle,
      message: STRINGS.common.webNotificationsDeniedMessage,
      buttons: [{ text: STRINGS.common.buttons.dismiss }],
    });
  }, [canAskNotificationPermission, isWeb, requestNotificationPermission]);

  return (
    <View style={styles.screen}>
      <AppHeader
        paddingTop={headerPaddingTop}
        paddingBottom={scale({ phone: 15, tablet: 21 })}
        backgroundColor={colors['0']}
        style={styles.header}>
        <AppHeader.Left>
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={strings.back}
            style={styles.headerButton}
            onPress={navigation.goBack}>
            <Stack direction="row" align="center" gap={scale(2)}>
              <SvgIcon name="IcBackBlue" size={scale(24)} color={colors.Blue} />
              <Text
                variant={
                  isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'
                }
                style={styles.headerActionText}>
                {strings.back}
              </Text>
            </Stack>
          </TouchableOpacity>
        </AppHeader.Left>
        <AppHeader.Title>
          <Text
            variant={
              isTablet
                ? 'Font Inter/Subtitle/Bold 14px'
                : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
            }
            fontWeight={isTablet ? undefined : '800'}
            style={styles.headerTitle}>
            {strings.title}
          </Text>
        </AppHeader.Title>
        <AppHeader.Right>
          <View style={styles.headerButton}>
            <Stack width={scale(24)} height={scale(24)} />
          </View>
        </AppHeader.Right>
      </AppHeader>

      <ScrollView
        contentContainerStyle={[
          styles.content,
          isTablet ? styles.contentConstrained : null,
          { paddingBottom: Math.max(insets.bottom, scale(24)) },
        ]}
        showsVerticalScrollIndicator={false}>
        {showPermissionBanner ? (
          <NotificationPermissionBanner
            message={strings.permissionWarning}
            onPress={handlePermissionPress}
            contentStyle={styles.bannerContent}
          />
        ) : null}

        <View style={styles.card}>
          {COACH_NOTIFICATION_PREFERENCE_ROWS.map((row, index) => (
            <Fragment key={row.key}>
              {isLoading || !preferences ? (
                <CoachNotificationPreferenceRowSkeleton />
              ) : (
                <CoachNotificationPreferenceRow
                  icon={row.icon}
                  title={row.title}
                  subtitle={row.description}
                  value={preferences[row.key]}
                  onValueChange={value => handleToggle(row.key, value)}
                />
              )}
              {index < COACH_NOTIFICATION_PREFERENCE_ROWS.length - 1 ? (
                <View style={styles.divider} />
              ) : null}
            </Fragment>
          ))}
        </View>
      </ScrollView>
    </View>
  );
}

const useStyles = createStyles(({ scale }) => ({
  screen: {
    flex: 1,
    backgroundColor: colors['1'],
  },
  header: {
    // Figma asks for 0.2px, but sub-hairline widths round away to nothing on device.
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors['10'],
  },
  headerButton: { paddingHorizontal: scale(4) },
  headerActionText: { color: colors.Blue, textTransform: 'uppercase' },
  headerTitle: { color: colors.Blue, textTransform: 'uppercase' },
  content: {
    paddingHorizontal: scale(16),
    paddingTop: scale(16),
    gap: scale(12),
    width: '100%',
    alignSelf: 'center',
  },
  contentConstrained: {
    maxWidth: scale(380),
  },
  bannerContent: {
    borderRadius: scale(2),
  },
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(2),
    overflow: 'hidden',
  },
  divider: {
    height: 1,
    backgroundColor: colors['2'],
  },
}));

export default CoachNotificationPreferencesScreen;
